// src/components/shell/UserMenu.js
// Avatar profil flottant en haut à droite + menu déroulant (compte, réglages, thème, déconnexion).
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  User,
  HelpCircle,
  LogOut,
  Moon,
  Sun,
  ExternalLink,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { SHELL_LABELS } from "./navConfig";
import { useTheme } from "../../contexts/ThemeContext";
import { toggleThemeWave } from "../../theme";
import useMe, { logout } from "../../hooks/useMe";
import { LanguageFlagSwitcher } from "./LanguageSwitcher";
import UserAvatar from "./UserAvatar";
import { openFullMarginProfile } from "../../utils/fullmargin";

export default function UserMenu() {
  const navigate = useNavigate();
  // Les libellés du menu compte étaient en dur : ils restaient en français
  // même après bascule en anglais.
  const { i18n } = useTranslation();
  const t = SHELL_LABELS[(i18n.language || "fr").startsWith("en") ? "en" : "fr"];
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const { user, initials } = useMe();
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleLogout = logout;

  const go = (to) => {
    navigate(to);
    setOpen(false);
  };

  // Le profil vit sur FullMargin (compte unique) : plus de page compte ici.
  const goFullMarginProfile = () => {
    openFullMarginProfile();
    setOpen(false);
  };

  const Item = ({ icon: Icon, label, onClick, danger, trailing: Trailing }) => (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
        danger
          ? "text-rose-600 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-500/10"
          : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/5"
      }`}
    >
      <Icon size={17} className={danger ? "" : "text-slate-400 dark:text-slate-400"} />
      <span className="flex-1 text-left">{label}</span>
      {Trailing && <Trailing size={14} className="shrink-0 text-slate-400 dark:text-slate-500" />}
    </button>
  );

  return (
    <div ref={boxRef} className="fixed top-4 right-5 z-[60] hidden lg:flex items-center gap-2">
      {/* Langue exposée en clair (drapeau), juste à gauche du thème */}
      <LanguageFlagSwitcher />

      {/* Bouton thème séparé, à gauche du profil */}
      <button
        onClick={(e) => toggleThemeWave(e.clientX, e.clientY)}
        aria-label={isDarkMode ? "Mode clair" : "Mode sombre"}
        title={isDarkMode ? "Mode clair" : "Mode sombre"}
        className="grid h-10 w-10 place-items-center rounded-full bg-white/80 dark:bg-[#181a2e]/80 backdrop-blur ring-1 ring-slate-200 dark:ring-white/10 shadow-sm text-slate-500 hover:text-violet-600 dark:text-slate-300 dark:hover:text-violet-300 transition"
      >
        {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* Profil + dropdown */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full p-1 pr-2 bg-white/80 dark:bg-[#181a2e]/80 backdrop-blur ring-1 ring-slate-200 dark:ring-white/10 shadow-sm hover:ring-violet-300 dark:hover:ring-violet-400/40 transition"
        aria-label="Menu du compte"
      >
        <UserAvatar photo={user?.photo} initials={initials} size={36} className="text-sm" />
        <ChevronDown
          size={16}
          className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 overflow-hidden rounded-2xl bg-white dark:bg-[#181a2e] shadow-xl ring-1 ring-slate-200 dark:ring-white/10 py-1.5">
          {/* Entête utilisateur */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-white/5">
            <UserAvatar photo={user?.photo} initials={initials} size={40} className="text-sm" />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-800 dark:text-white">
                {user?.username || "…"}
              </div>
              <div className="truncate text-xs text-slate-400 dark:text-slate-400">
                {user?.email || <span className="capitalize">{user?.plan || "free"}</span>}
              </div>
            </div>
          </div>

          <div className="py-1">
            <Item
              icon={User}
              label={t.account}
              trailing={ExternalLink}
              onClick={goFullMarginProfile}
            />
            <Item icon={HelpCircle} label={t.help} onClick={() => go("/help")} />
          </div>

          <div className="border-t border-slate-100 dark:border-white/5 py-1">
            <Item icon={LogOut} label={t.logout} onClick={handleLogout} danger />
          </div>
        </div>
      )}
    </div>
  );
}
